/**
 * The Shout-out Wall's pure model: studio-time month keys, the school-year
 * window the wall shows, month grouping, search, and the small pieces the
 * redesigned page is assembled from (award tiles, the Jr Staff MVP card, the
 * month rail).
 *
 * WHY STUDIO TIME, AND WHY IT LIVES HERE
 * --------------------------------------
 * monthKey() in lib/studentAwards.ts reads the VIEWER's clock
 * (date.getFullYear() / date.getMonth()), which is right for a period the
 * viewer is picking by hand and wrong for filing a stored instant. The wall is
 * organised BY MONTH, so a shout-out posted at 11:40pm on the 31st filed into
 * a different month for a teacher in Chicago than for a parent one timezone
 * east - the same board, two different answers, no error anywhere. That is the
 * dayKey trap in docs/lessons.md ("a test can pass for the wrong reason...
 * because CI runs in UTC"), so the month a stored instant belongs to is pinned
 * to the studio's own clock here, and the test pins a NON-studio TZ so a
 * regression to local time cannot pass.
 *
 * NO RUNTIME IMPORTS beyond fuzzyMatch.ts, same rule as lib/tonight.ts and
 * lib/awardsDisplay.ts and the same carve-out parentBoardThreads.ts records:
 * hub/tests/shoutout-wall.test.mjs drives this file directly under
 * node --experimental-strip-types, and anything imported here would load
 * with it (the firebase SDK most dangerously). fuzzyMatch.ts has zero
 * imports of its own, so it is safe.
 *
 * schoolYearKeyForPeriod lives here rather than in lib/studentAwards.ts, which
 * re-exports it, so there is exactly ONE definition of where a school year
 * starts. Two copies of that fact would drift.
 */

import { looseMatchWithBody } from './fuzzyMatch.ts';
import { recognizedAwardMonthKey } from './studentAwardSchedule.ts';

/** The studio's own clock. Everything month-shaped on the wall is read in it. */
export const STUDIO_TIME_ZONE = 'America/Chicago';

/**
 * 'YYYY-MM' for an instant, read as studio wall-clock time.
 *
 * Falls back to the viewer's own clock only if Intl cannot produce the zone at
 * all, which no supported browser does - a wrong month beats a crash on a
 * celebration board.
 */
export function studioMonthKey(value: Date | number = Date.now()): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(d);
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    if (year.length === 4 && month.length === 2) return `${year}-${month}`;
  } catch {
    // fall through to the local reading below
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * The school year a 'YYYY-MM' period belongs to, as 'YYYY-YYYY'. August starts
 * the year, matching how the studio counts a season.
 */
export function schoolYearKeyForPeriod(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  const start = month >= 8 ? year : year - 1;
  return `${start}-${start + 1}`;
}

/**
 * The epoch millisecond floor to FETCH a school year from.
 *
 * Deliberately UTC midnight on August 1, which is 7pm on July 31 in Chicago:
 * a few hours EARLY, so the query can never miss something the studio would
 * count as this year. It is a cheap bound on how much a family downloads, not
 * the definition of the year - the exact boundary is decided per item by
 * schoolYearKeyForPeriod(studioMonthKey(...)) once the data is in hand.
 */
export function schoolYearStartMs(schoolYearKey: string): number {
  const startYear = Number(schoolYearKey.split('-')[0]);
  if (!Number.isFinite(startYear) || startYear <= 0) return 0;
  return Date.UTC(startYear, 7, 1, 0, 0, 0, 0);
}

/** Anything the wall can file into a month. */
export interface WallDated {
  periodKey: string;
}

/**
 * Every month of THIS SCHOOL YEAR the wall has something to show, newest
 * first, always including the current month so the board is never chipless.
 *
 * There is no count cap here on purpose. A school year is at most 12 chips,
 * and every month with public recognition remains reachable.
 */
export function wallMonths(
  items: readonly WallDated[],
  schoolYearKey: string,
  currentPeriodKey: string
): string[] {
  const months = new Set<string>();
  if (schoolYearKeyForPeriod(currentPeriodKey) === schoolYearKey) months.add(currentPeriodKey);
  for (const item of items) {
    if (!item.periodKey) continue;
    if (schoolYearKeyForPeriod(item.periodKey) !== schoolYearKey) continue;
    months.add(item.periodKey);
  }
  return [...months].sort((a, b) => b.localeCompare(a));
}

/** How many items sit in each month, for the chip badges. */
export function countsByMonth(items: readonly WallDated[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (!item.periodKey) continue;
    counts.set(item.periodKey, (counts.get(item.periodKey) ?? 0) + 1);
  }
  return counts;
}

/**
 * Does this row match the search? `shortFields` is every short field a
 * parent is actually typing a name or a label toward - a dancer's name, the
 * award or shoutout kind, whoever gave it - and forgives a typo the same as
 * every other search box in the app (packet F1, DECISIONS 33 item 5).
 * `body` is the shoutout's own free-text note, up to 500 characters: a
 * prose field, not a name, so it stays on the exact per-term substring pass
 * the wall always used, the same shape Tasks.tsx uses for its description.
 * Empty or whitespace-only searches match everything, so an empty box never
 * hides the wall.
 */
export function matchesSearch(
  query: string,
  shortFields: readonly (string | null | undefined)[],
  body: readonly (string | null | undefined)[] = []
): boolean {
  return looseMatchWithBody(shortFields, body, query);
}

// ---------------------------------------------------------------------------
// The redesigned wall (Bobbie's Claude Design export, 2026-09-29,
// docs/briefs/shoutout-wall/). Still pure: the page reads real wall cards and
// shout-outs through these, and hub/tests/shoutout-wall.test.mjs drives them
// directly.
// ---------------------------------------------------------------------------

/** The three kinds of card the wall can hold. `jr-staff-mvp` is the Jr Staff
 *  MVP; nothing writes it until the MVP wall step ships, so every reader
 *  treats it as possibly absent. */
export type WallAwardType = 'student-of-month' | 'jr-spotlight' | 'jr-staff-mvp';

/** Tiles shown per award group before "Show all". The export's own number. */
export const WALL_PREVIEW_TILES = 6;

/** The label the wall gives the MVP card. Same words as lib/awardStars.ts. */
export const WALL_MVP_LABEL = 'Jr Staff MVP';

/** What the wall reads off a public win card. Names and awards only: there is
 *  no note here and there never will be (Bobbie, 2026-08-05). */
export interface WallWinLike {
  id: string;
  periodKey: string;
  awardType: string;
  studentName: string;
  fromName?: string | null;
}

/**
 * The teacher who picked, from a wall card that carries `fromName`, or
 * undefined. Read defensively: the field is not written yet, an old card
 * never has it, and a stored value that is not text, is blank or is really an
 * email address is dropped rather than shown (the same posture as
 * lib/storedName.ts, restated here because this file imports nothing).
 */
export function cleanFromName(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  // Look for the '@' in the WHOLE value first: cutting to 180 characters before
  // the test would let an email that starts inside the cap through as a stub
  // ("Ms. Tara kimberly.") once its '@' had been cut off.
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) return undefined;
  return trimmed.slice(0, 180).trimEnd();
}

/** "from Ms Shay", or '' when the card does not say who picked. */
export function awardTileFrom(fromName: unknown): string {
  const name = cleanFromName(fromName);
  return name ? `from ${name}` : '';
}

/** One month's cards of one award type, A to Z by dancer. */
export function winsOfKind<T extends WallWinLike>(
  wins: readonly T[],
  periodKey: string,
  awardType: WallAwardType
): T[] {
  return wins
    .filter((w) => w.periodKey === periodKey && w.awardType === awardType)
    .sort((a, b) => a.studentName.localeCompare(b.studentName) || a.id.localeCompare(b.id));
}

/**
 * The month's Jr Staff MVP card, or null. ONE a month by design; if a stray
 * second card ever exists the lowest id wins so every viewer sees the same
 * one rather than whichever the listener delivered first.
 */
export function mvpWinForMonth<T extends WallWinLike>(wins: readonly T[], periodKey: string): T | null {
  const cards = winsOfKind(wins, periodKey, 'jr-staff-mvp');
  if (cards.length === 0) return null;
  return [...cards].sort((a, b) => a.id.localeCompare(b.id))[0];
}

/**
 * Which tiles show. Not searching: the first WALL_PREVIEW_TILES, or all of
 * them once expanded, with a toggle whenever there are more than the preview.
 * Searching: every match, and no toggle, because a search that hides half its
 * own matches behind a button is a search that lies (the export does the
 * same).
 */
export function tileWindow<T>(
  items: readonly T[],
  expanded: boolean,
  searching: boolean
): { shown: T[]; showToggle: boolean } {
  if (searching) return { shown: [...items], showToggle: false };
  const overflow = items.length > WALL_PREVIEW_TILES;
  if (!overflow) return { shown: [...items], showToggle: false };
  return { shown: expanded ? [...items] : items.slice(0, WALL_PREVIEW_TILES), showToggle: true };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** "September" and "2026" for a 'YYYY-MM' key; null when it is not one. Fixed
 *  English names, so the heading never depends on the viewer's locale. */
export function monthParts(periodKey: string): { name: string; year: string } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { name: MONTH_NAMES[month - 1], year: match[1] };
}

/** "September 2026" for a 'YYYY-MM' key, the key itself when it is not one. */
export function wallMonthLabel(periodKey: string): string {
  const parts = monthParts(periodKey);
  return parts ? `${parts.name} ${parts.year}` : periodKey;
}

/** The month rail's label: "Sep '26" with a typographic apostrophe. */
export function railLabel(periodKey: string): string {
  const parts = monthParts(periodKey);
  if (!parts) return periodKey;
  return `${parts.name.slice(0, 3)} ’${parts.year.slice(2)}`;
}

/** "1 shout-out" or "3 shout-outs". */
export function shoutoutCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'shout-out' : 'shout-outs'}`;
}

/** "1 recipient" or "12 recipients". */
export function recipientCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'recipient' : 'recipients'}`;
}

/**
 * "Sep 28", the day an instant falls on in STUDIO time, so the date printed on
 * a card and the month it is filed under always agree (a shout-out posted at
 * 11:40pm Central on the 31st must not read "Sep 1" to a viewer one zone east
 * while sitting under August). null, a stamp that has not resolved yet, reads
 * "Just now".
 */
export function studioShortDate(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return 'Just now';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE,
      month: 'short',
      day: 'numeric',
    }).format(new Date(ms));
  } catch {
    return 'Just now';
  }
}

// ---------------------------------------------------------------------------
// The rail, and the months folded below it (Bobbie, 2026-09-29 about 3:40pm
// Central: "Worried this could get long with time. so mabe this month, last
// month and then a drop down for past onths?" and "I like how these compact at
// the bottom.. and then shiuld compact by year after that").
// ---------------------------------------------------------------------------

/** 'YYYY-MM' for a well-formed month key, else null. Used for `?month=`. */
export function readWallMonthParam(raw: string | null | undefined): string | null {
  return typeof raw === 'string' && monthParts(raw) ? raw : null;
}

/** The month before a 'YYYY-MM' key ('2027-01' gives '2026-12'), or '' for a bad key. */
export function previousMonthKey(periodKey: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
  if (!match || !monthParts(periodKey)) return '';
  const year = Number(match[1]);
  const month = Number(match[2]);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

/** The school year before a 'YYYY-YYYY' key ('2026-2027' gives '2025-2026'), or '' for a bad key. */
export function previousSchoolYearKey(schoolYearKey: string): string {
  const match = /^(\d{4})-(\d{4})$/.exec(schoolYearKey);
  if (!match) return '';
  const start = Number(match[1]);
  return `${start - 1}-${start}`;
}

/**
 * AN AWARD SITS UNDER THE MONTH IT RECOGNIZES (Bobbie, 2026-10-18, DECISIONS
 * 55 carried onto the wall): "the September dancers of the month and
 * spotlight of the month and all of those should be showing up on September
 * ... It needs to say coming soon because it won't be released until the
 * beginning of November because it's a reflection on the previous month."
 *
 * A wall card is STORED under its round's period, the month it is posted in
 * (2026-10 holds the September winners). Every card is re-filed here, once, as
 * it reaches the page, so the month rail, the counts, search, the folded
 * months and `?month=` all read the month the award names. Stored data is
 * never re-keyed. The month being lived in therefore shows Coming soon until
 * its winners go up early the next month.
 */
export function fileWallCardsByRecognizedMonth<T extends { periodKey: string }>(cards: readonly T[]): T[] {
  return cards.map((card) => ({ ...card, periodKey: recognizedAwardMonthKey(card.periodKey) }));
}

/**
 * The stored period whose cards recognize a school year's LAST month: July's
 * winners go up in August, which is filed under the next school year
 * ('2025-2026' gives '2026-08'). An older year read pulls this month too, or
 * its July would show without its winners. '' for a bad key.
 */
export function closingAwardPeriodForSchoolYear(schoolYearKey: string): string {
  const match = /^(\d{4})-(\d{4})$/.exec(schoolYearKey);
  return match ? `${match[2]}-08` : '';
}

/** "2025 to 2026" for '2025-2026'. */
export function schoolYearLabel(schoolYearKey: string): string {
  const match = /^(\d{4})-(\d{4})$/.exec(schoolYearKey);
  return match ? `${match[1]} to ${match[2]}` : schoolYearKey;
}

/**
 * Which months ride the sticky rail: this month and last month, ALWAYS, newest
 * first (Bobbie: "this month, last month"). Last month rides even when it
 * belongs to the school year before (every August it is July), and even when
 * it is empty (its pill says 0). Everything else folds below.
 */
export function railMonthKeys(currentPeriodKey: string): string[] {
  const last = previousMonthKey(currentPeriodKey);
  return last ? [currentPeriodKey, last] : [currentPeriodKey];
}

/** The months NOT on the rail, newest first: what folds under "Earlier months". */
export function earlierMonthKeys(months: readonly string[], railKeys: readonly string[]): string[] {
  return months.filter((m) => !railKeys.includes(m)).sort((a, b) => b.localeCompare(a));
}

/** Month keys grouped by school year, newest year first, each year's months newest first. */
export function groupMonthsBySchoolYear(
  periodKeys: readonly string[]
): Array<{ schoolYearKey: string; months: string[] }> {
  const byYear = new Map<string, string[]>();
  for (const key of new Set(periodKeys)) {
    if (!monthParts(key)) continue;
    const year = schoolYearKeyForPeriod(key);
    byYear.set(year, [...(byYear.get(year) ?? []), key]);
  }
  return [...byYear.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([schoolYearKey, months]) => ({ schoolYearKey, months: months.sort((a, b) => b.localeCompare(a)) }));
}

/** "2 Student of the Month, 3 Monthly Spotlight, 1 Jr Staff MVP": only the nonzero parts. */
export function awardCountLine(parts: ReadonlyArray<{ label: string; count: number }>): string {
  return parts
    .filter((p) => p.count > 0)
    .map((p) => `${p.count} ${p.label}`)
    .join(', ');
}

/**
 * The count line on a folded month or year card. Counts ONLY what the viewer
 * may see: award counts when they may see awards (falling back to the
 * shout-out count if the month has no award yet), otherwise the shout-out
 * count alone.
 */
export function foldedCountLine(input: {
  showAwards: boolean;
  awards: ReadonlyArray<{ label: string; count: number }>;
  shoutouts: number;
}): string {
  if (input.showAwards) {
    const awards = awardCountLine(input.awards);
    if (awards) return awards;
  }
  return shoutoutCountLabel(input.shoutouts);
}

// ---------------------------------------------------------------------------
// Years older than the one before (Bobbie, 2026-09-29: "Forever everywhere").
// The wall reads this school year and the one before it up front. Every school
// year before that gets a folded card that reads ITS OWN year, one bounded
// query, only when the card is opened.
// ---------------------------------------------------------------------------

/**
 * The first school year the wall can hold anything for. There is no cheap way
 * to ask Firestore which years have a shout-out, so the older-year cards run
 * back to this fixed floor: the Hub's wall cards begin in 2026 (a backfill can
 * reach the months before August) and shout-outs in September 2026. Nothing
 * earlier exists.
 */
export const WALL_FIRST_SCHOOL_YEAR = '2025-2026';

/**
 * The instant to STOP fetching a school year at. The next year's floor
 * (schoolYearStartMs) is 7pm on July 31 in Chicago, hours before the studio's
 * own year turns over, so a post made between then and midnight that night is
 * filed (studio time) in July and belongs to THIS year while sitting past the
 * next year's floor. Six hours past the floor covers that evening in either
 * daylight-saving state. Month filing still decides where each post lands;
 * anything caught twice is merged by id.
 */
export function schoolYearEndMs(schoolYearKey: string): number {
  const startYear = Number(schoolYearKey.split('-')[0]);
  if (!Number.isFinite(startYear) || startYear <= 0) return 0;
  return Date.UTC(startYear + 1, 7, 1, 6, 0, 0, 0);
}

/**
 * The school years that get a lazily loaded folded card: EVERY year before the
 * current one, newest first, back to `first`. Nothing of them is read until the
 * card is opened (the year before used to be read up front, about three times
 * the base cost on every open). For 2027-2028 that is 2026-2027 and 2025-2026;
 * for 2025-2026 it is none.
 */
export function olderSchoolYearKeys(
  currentSchoolYear: string,
  first: string = WALL_FIRST_SCHOOL_YEAR
): string[] {
  const keys: string[] = [];
  let year = previousSchoolYearKey(currentSchoolYear);
  let guard = 0;
  while (year && year >= first && guard++ < 200) {
    keys.push(year);
    year = previousSchoolYearKey(year);
  }
  return keys;
}

/** The first of the month as an instant, deliberately early (UTC midnight, hours
 *  before the studio's own midnight) so it is a cheap superset of the month. */
export function monthFloorMs(periodKey: string): number {
  const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
  if (!match || !monthParts(periodKey)) return 0;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, 1);
}

/**
 * Where the wall's up-front shout-out read starts: the EARLIER of this school
 * year's floor and the first of last month, so last month's shout-outs are
 * always there (every August, last month is July and sits before the school
 * year). Everything older is read only when its card is opened.
 */
export function wallReadFloorMs(currentPeriodKey: string): number {
  if (!monthParts(currentPeriodKey)) return 0;
  const yearFloor = schoolYearStartMs(schoolYearKeyForPeriod(currentPeriodKey));
  const lastFloor = monthFloorMs(previousMonthKey(currentPeriodKey));
  return lastFloor > 0 ? Math.min(yearFloor, lastFloor) : yearFloor;
}

/**
 * The first instant of the NEXT studio month after `nowMs`, so a page left open
 * can re-file "this month" the moment it turns over. Studio midnight is 05:00
 * UTC in daylight time and 06:00 in standard time, so it starts four hours into
 * the first of the month and walks forward until the studio month changes.
 * 0 for a bad value.
 */
export function nextStudioMonthStartMs(nowMs: number): number {
  if (!Number.isFinite(nowMs)) return 0;
  const key = studioMonthKey(nowMs);
  const match = /^(\d{4})-(\d{2})$/.exec(key);
  if (!match) return 0;
  const start = Date.UTC(Number(match[1]), Number(match[2]), 1, 4);
  for (let hour = 0; hour < 12; hour++) {
    const at = start + hour * 3_600_000;
    if (studioMonthKey(at) !== key) return at;
  }
  return start + 12 * 3_600_000;
}

/** The longest a page waits before re-checking which studio month it is. */
export const MONTH_RECHECK_CAP_MS = 24 * 60 * 60 * 1000;

/**
 * Is EVERYTHING filed under this studio month actually in hand? The up-front
 * read starts at UTC midnight on the first of a month, a few hours before the
 * studio's own midnight, so the hours in between are filed (studio time) under
 * the month BEFORE it and that month is only partly loaded. Months that are
 * whole: any month of this school year (the read starts at or before its first
 * day), last month (the read starts at or before ITS first day), and any month
 * of an older school year whose own bounded query has finished. Anything else
 * is a fragment, and showing it would put a false count on a real month.
 */
export function isCompleteWallMonth(
  periodKey: string,
  view: { currentSchoolYear: string; lastMonthKey: string; loadedYears: readonly string[] }
): boolean {
  const year = schoolYearKeyForPeriod(periodKey);
  return (
    year === view.currentSchoolYear ||
    (!!view.lastMonthKey && periodKey === view.lastMonthKey) ||
    view.loadedYears.includes(year)
  );
}

/**
 * The older school year a `?month=` deep link needs read before it can show,
 * or null when the month is already whole up front (this school year, or last
 * month) or is not in any year the wall keeps. Lets the page start reading and
 * open that year's card, then show the month, instead of falling back to All.
 */
export function olderYearForMonth(month: string | null, currentPeriodKey: string): string | null {
  if (!month || !monthParts(month) || !monthParts(currentPeriodKey)) return null;
  const currentYear = schoolYearKeyForPeriod(currentPeriodKey);
  if (month === previousMonthKey(currentPeriodKey)) return null;
  const year = schoolYearKeyForPeriod(month);
  if (year === currentYear) return null;
  return olderSchoolYearKeys(currentYear).includes(year) ? year : null;
}

/** How long a `?month=` deep link into an older school year waits for that year
 *  before the wall stops waiting and settles on All for good. */
export const DEEP_LINK_WAIT_MS = 15_000;
